import { Button, Field, Input } from "@dl/web-core-js/ui";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PublicClient, type DisplayCollectionDTO, type LazyArtworkDTO } from "../../api";
import { lineImage } from "../../components/cells";
import { verifyPassword } from "../../lib/password";

const SIZES = [
  { key: "small", labelKey: "gallery.size.small", fallback: "Small", columns: 6 },
  { key: "medium", labelKey: "gallery.size.medium", fallback: "Medium", columns: 4 },
  { key: "large", labelKey: "gallery.size.large", fallback: "Large", columns: 3 },
  { key: "xlarge", labelKey: "gallery.size.xlarge", fallback: "XLarge", columns: 2 },
] as const;

/**
 * The public, password-gated gallery.
 *
 * It lives outside the app shell and outside the session: the only endpoints
 * it touches are `@AnonymousAllowed`, and the password is checked against the
 * collection's own BCrypt hash in the browser — the same arrangement the
 * Vaadin view used, and the reason a link can be handed to a buyer with no
 * account.
 */
export function GalleryPage({ uuid }: { uuid: string }) {
  const { t } = useTranslation();
  const client = useMemo(() => new PublicClient(), []);
  const [collection, setCollection] = useState<DisplayCollectionDTO | null>(null);
  const [artworks, setArtworks] = useState<Map<number, LazyArtworkDTO>>(new Map());
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [unlocked, setUnlocked] = useState(false);
  const [checking, setChecking] = useState(false);
  const [columns, setColumns] = useState(2);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const found = await client.getDisplayCollectionByUUID(uuid);
        if (cancelled) return;
        setCollection(found);
        if (found) {
          const ids = (found.images ?? [])
            .map((line) => line.artwork_id)
            .filter((value): value is number => Boolean(value));
          const list = await client.getLazyArtworksByIds(Array.from(new Set(ids)));
          if (!cancelled) setArtworks(new Map(list.map((entry) => [entry.id, entry])));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, uuid]);

  const submit = async () => {
    if (!collection) return;
    setChecking(true);
    try {
      const ok = await verifyPassword(password, collection.password ?? "");
      if (ok) {
        setUnlocked(true);
        setError(null);
      } else {
        setError("Invalid password");
      }
    } finally {
      setChecking(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-ground">
        <span className="size-6 animate-spin rounded-full border-2 border-accent border-t-transparent" />
      </div>
    );
  }

  if (!collection) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-ground">
        <p className="text-[13px] text-ink-muted">{t("gallery.not-found", "Collection not found.")}</p>
      </div>
    );
  }

  if (!unlocked) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-ground px-4">
        <h1 className="text-xl font-semibold text-ink">Login</h1>
        <div className="flex w-full max-w-sm items-end gap-2">
          <Field label="Password" error={error ?? undefined} className="flex-1">
            <Input
              type="password"
              value={password}
              placeholder="Enter password"
              onChange={(event) => setPassword(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") void submit();
              }}
            />
          </Field>
          <Button variant="primary" loading={checking} onClick={() => void submit()}>
            Submit
          </Button>
        </div>
      </div>
    );
  }

  const images = collection.images ?? [];

  return (
    <div className="min-h-dvh overflow-auto bg-ground px-4 py-6 lg:px-10">
      <header className="flex flex-col items-center gap-1">
        <h1 className="text-2xl font-semibold text-ink">{t("gallery.title", "Clicart Collection Preview")}</h1>
        <p className="text-[15px] text-ink-muted">{collection.name}</p>
      </header>

      <div className="mt-4 flex justify-center gap-2">
        {SIZES.map((size) => (
          <button
            key={size.key}
            type="button"
            onClick={() => setColumns(size.columns)}
            className={
              columns === size.columns
                ? "rounded-md bg-accent px-3 py-1 text-[13px] font-medium text-ink-on-accent"
                : "rounded-md border border-line px-3 py-1 text-[13px] text-ink-muted hover:text-ink"
            }
          >
            {t(size.labelKey, size.fallback)}
          </button>
        ))}
      </div>

      <hr className="my-4 border-line" />

      <div
        className="grid gap-6"
        style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
      >
        {images.map((line) => {
          const url = lineImage(line)?.url;
          if (!url) return null;
          const artwork = line.artwork_id ? artworks.get(line.artwork_id) : undefined;
          return (
            <figure key={`${line.owner}-${line.image_id}`} className="flex flex-col items-center gap-2">
              <img
                src={url}
                alt={artwork?.metadata.title ?? ""}
                onClick={() => window.open(url, "_blank", "noreferrer")}
                className="w-full cursor-pointer shadow-[0_4px_8px_0_rgba(0,0,0,0.2),0_6px_20px_0_rgba(0,0,0,0.19)]"
              />
              <figcaption className="flex flex-col items-center text-center">
                <span className="text-[14px] text-ink-faint">{artwork?.metadata.code ?? ""}</span>
                <span className="text-[14px] font-medium text-ink">
                  {artwork?.metadata.title ?? ""}
                </span>
                <span className="text-[12px] text-ink-muted">
                  {`by: ${artwork?.metadata.artist ?? "unknown"}`}
                </span>
              </figcaption>
            </figure>
          );
        })}
      </div>
    </div>
  );
}
